/**
 * Helpers for `kind: "image"` artifacts.
 *
 * Image artifacts store their renderable payload as a JSON-stringified
 * object inside the existing `content` text column so the canvas can pick
 * up `url`, mime type, dimensions, and generation lineage without a schema
 * migration. Parsing is best-effort — bad JSON falls back to `null` so the
 * viewer can render an empty/error state instead of crashing.
 *
 * @module lib/artifacts/image
 */

import type { ImageArtifactContent } from "@taqat/db";

export type { ImageArtifactContent };

/**
 * Parse an image artifact's `content` string into a structured payload.
 * Returns `null` for empty content (artifact still streaming) or any
 * malformed JSON so callers can render a placeholder.
 */
export function parseImageArtifactContent(
  content: string,
): ImageArtifactContent | null {
  if (!content || !content.trim()) return null;
  try {
    const parsed = JSON.parse(content) as Partial<ImageArtifactContent>;
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.url !== "string" || parsed.url.length === 0) return null;
    return {
      url: parsed.url,
      mimeType:
        typeof parsed.mimeType === "string" && parsed.mimeType.length > 0
          ? parsed.mimeType
          : "image/png",
      width: typeof parsed.width === "number" ? parsed.width : undefined,
      height: typeof parsed.height === "number" ? parsed.height : undefined,
      sizeBytes:
        typeof parsed.sizeBytes === "number" ? parsed.sizeBytes : undefined,
      prompt: typeof parsed.prompt === "string" ? parsed.prompt : undefined,
      model: typeof parsed.model === "string" ? parsed.model : undefined,
      seed: typeof parsed.seed === "number" ? parsed.seed : undefined,
    };
  } catch {
    return null;
  }
}

/** Human-readable byte count (e.g. `842 KB`, `1.4 MB`) for the metadata sidebar. */
export function formatImageSize(bytes: number | undefined): string | null {
  if (bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Reduce two integers to their greatest common divisor so we can render
 * "1280 × 720" as the friendlier "16 : 9" in the sidebar.
 */
function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/** Returns `"16 : 9"`-style aspect label or `null` if dimensions are unknown. */
export function formatAspectRatio(
  width: number | undefined,
  height: number | undefined,
): string | null {
  if (!width || !height) return null;
  const divisor = gcd(width, height);
  if (divisor === 0) return null;
  return `${width / divisor} : ${height / divisor}`;
}

/** Friendly format label inferred from mime type, e.g. `image/png` → `PNG`. */
export function formatLabel(mimeType: string | undefined): string {
  if (!mimeType) return "Image";
  const subtype = mimeType.split("/").pop() ?? "";
  if (!subtype) return "Image";
  if (subtype === "svg+xml") return "SVG";
  return subtype.toUpperCase();
}
